import React, { useState, useEffect, memo } from "react";
import {
  FileIcon,
  DownloadIcon,
  UploadIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  XIcon,
  FileSpreadsheetIcon,
  InfoIcon,
  HelpCircleIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Sample template structure for the Excel file
const excelTemplateColumns = [
  {
    name: "Product Name",
    required: true,
    description:
      "Full name of the machinery (e.g., Caterpillar Excavator 320D)",
  },
  {
    name: "Category",
    required: true,
    description:
      "Must match one of the predefined categories (e.g., Construction)",
  },
  {
    name: "Subcategory",
    required: true,
    description:
      "Must match a subcategory within the selected Category (e.g., Excavators)",
  },
  {
    name: "Product Category",
    required: true,
    description:
      "Must match a product category within the selected Subcategory",
  },
  {
    name: "Brand",
    required: false,
    description: "Brand name (e.g., Caterpillar, John Deere)",
  },
  {
    name: "Model",
    required: false,
    description: "Specific model number (e.g., 320D)",
  },
  {
    name: "Condition",
    required: true,
    description: "Must be one of: New, Used, Refurbished",
  },
  {
    name: "Price",
    required: true,
    description: "Price in USD (numeric value only, no currency symbol)",
  },
  {
    name: "Year",
    required: false,
    description: "Year of manufacture (e.g., 2020)",
  },
  {
    name: "Hours Used",
    required: false,
    description:
      "Number of hours the machinery has been used (for used machinery)",
  },
  {
    name: "Description",
    required: false,
    description: "Detailed description of the machinery",
  },
  {
    name: "Location",
    required: false,
    description: "Location of the machinery (e.g., Mumbai, Maharashtra)",
  },
  {
    name: "SKU",
    required: false,
    description: "Stock Keeping Unit - unique identifier for your product",
  },
  {
    name: "Quantity",
    required: false,
    description: "Number of units available (default: 1)",
  },
  {
    name: "In Stock",
    required: false,
    description: "Enter 'Yes' or 'No' (default: Yes)",
  },
  {
    name: "Featured",
    required: false,
    description: "Enter 'Yes' or 'No' (default: No)",
  },
  {
    name: "Local Pickup",
    required: false,
    description: "Enter 'Yes' or 'No' (default: Yes)",
  },
  {
    name: "Domestic Shipping",
    required: false,
    description: "Enter 'Yes' or 'No' (default: No)",
  },
  {
    name: "International Shipping",
    required: false,
    description: "Enter 'Yes' or 'No' (default: No)",
  },
  {
    name: "Image URLs",
    required: false,
    description: "Comma-separated list of image URLs",
  },
];

export default memo(function BulkProductUpload() {
  console.log('🔄 BulkProductUpload render/creation');
  
  const [file, setFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<
    "idle" | "validating" | "uploading" | "success" | "error"
  >("idle");
  const [progress, setProgress] = useState(0);
  const [validationResults, setValidationResults] = useState<{
    valid: boolean;
    totalRows: number;
    validRows: number;
    invalidRows: number;
    errors: Array<{ row: number; column: string; message: string }>;
  } | null>(null);

  // Load saved state from localStorage on component mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('bulkUploadState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        console.log('📥 Loading saved bulk upload state from localStorage:', parsedState);
        
        setUploadState(parsedState.uploadState || 'idle');
        setProgress(parsedState.progress || 0);
        setValidationResults(parsedState.validationResults || null);
        
        // Note: We can't restore the actual File object from localStorage
        // but we can show the filename if it was saved
        if (parsedState.fileName) {
          console.log('📁 Previously selected file:', parsedState.fileName);
        }
      }
    } catch (error) {
      console.error('Error loading saved bulk upload state:', error);
      localStorage.removeItem('bulkUploadState');
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      const stateToSave = {
        uploadState,
        progress,
        validationResults,
        fileName: file?.name || null,
        lastSaved: new Date().toISOString()
      };
      
      // Only save if there's meaningful state
      if (uploadState !== 'idle' || file || validationResults) {
        localStorage.setItem('bulkUploadState', JSON.stringify(stateToSave));
        console.log('💾 Saving bulk upload state to localStorage');
      }
    } catch (error) {
      console.error('Error saving bulk upload state:', error);
    }
  }, [uploadState, progress, validationResults, file]);

  // Clear saved state
  const clearSavedState = () => {
    localStorage.removeItem('bulkUploadState');
    console.log('🗑️ Cleared saved bulk upload state from localStorage');
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check if file is Excel
      if (
        selectedFile.type === "application/vnd.ms-excel" ||
        selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setFile(selectedFile);
        setUploadState("idle");
        setValidationResults(null);
      } else {
        alert("Please select a valid Excel file (.xls or .xlsx)");
      }
    }
  };

  // Download template function
  const downloadTemplate = () => {
    // In a real implementation, this would generate and download an Excel template
    console.log("Downloading template...");
    // Mock download - in a real app, you would create and serve an actual Excel file
    alert("Template download started. (This is a mock implementation)");
  };

  // Validate the uploaded file
  const validateFile = () => {
    if (!file) return;

    setUploadState("validating");
    setProgress(0);

    // Simulate validation process with progress
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + 10;
        if (newProgress >= 100) {
          clearInterval(interval);

          // Mock validation results - in a real app, you would parse the Excel file and validate the data
          const mockValidationResults = {
            valid: false,
            totalRows: 15,
            validRows: 12,
            invalidRows: 3,
            errors: [
              {
                row: 3,
                column: "Category",
                message:
                  "Invalid category 'Heavy Machinery'. Must be one of the predefined categories.",
              },
              {
                row: 7,
                column: "Price",
                message: "Price must be a positive number.",
              },
              {
                row: 12,
                column: "Condition",
                message:
                  "Invalid condition 'Like New'. Must be one of: New, Used, Refurbished.",
              },
            ],
          };

          setValidationResults(mockValidationResults);
          setUploadState(mockValidationResults.valid ? "idle" : "error");
          return 100;
        }
        return newProgress;
      });
    }, 300);
  };

  // Upload the validated file
  const uploadFile = () => {
    if (!file || !validationResults?.valid) return;

    setUploadState("uploading");
    setProgress(0);

    // Simulate upload process with progress
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + 5;
        if (newProgress >= 100) {
          clearInterval(interval);
          setUploadState("success");
          return 100;
        }
        return newProgress;
      });
    }, 200);
  };

  // Reset the form
  const resetForm = () => {
    setFile(null);
    setUploadState("idle");
    setProgress(0);
    setValidationResults(null);
    clearSavedState();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Bulk Product Upload</CardTitle>
          <CardDescription>
            Upload multiple products at once using an Excel file
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Template Download Section */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-full">
                <FileSpreadsheetIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium mb-1">Excel Template</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Download our Excel template to ensure your data is formatted
                  correctly. Fill in the template with your product information
                  and upload it below.
                </p>
                <Button
                  onClick={downloadTemplate}
                  variant="outline"
                  className="gap-2"
                >
                  <DownloadIcon className="h-4 w-4" />
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* Template Structure */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Template Structure</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <HelpCircleIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="w-80 p-4">
                    <p className="text-sm">
                      The Excel template contains all the necessary columns for
                      adding products. Required columns are marked with an
                      asterisk (*). Make sure to follow the format guidelines
                      for each column.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="border rounded-md overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">Column Name</TableHead>
                      <TableHead className="w-1/6">Required</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {excelTemplateColumns.map((column, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {column.name}
                          {column.required && (
                            <span className="text-destructive ml-1">*</span>
                          )}
                        </TableCell>
                        <TableCell>{column.required ? "Yes" : "No"}</TableCell>
                        <TableCell className="text-sm">
                          {column.description}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Upload Excel File</h3>
              {file && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetForm}
                  className="h-8 gap-1 text-muted-foreground"
                >
                  <XIcon className="h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>

            {!file ? (
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="excel-upload"
                  className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <UploadIcon className="w-8 h-8 mb-3 text-gray-400" />

                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Excel files only (.xls, .xlsx)
                    </p>
                  </div>
                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-muted/50 rounded-lg">
                  <FileIcon className="h-8 w-8 mr-3 text-blue-500" />

                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  {uploadState === "idle" && (
                    <Button onClick={validateFile} className="ml-4">
                      Validate
                    </Button>
                  )}
                </div>

                {uploadState === "validating" && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Validating file...</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                )}

                {uploadState === "uploading" && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Uploading products...</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                )}

                {validationResults && (
                  <div className="space-y-4">
                    {validationResults.valid ? (
                      <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900">
                        <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />

                        <AlertTitle>Validation Successful</AlertTitle>
                        <AlertDescription>
                          All {validationResults.totalRows} products are valid
                          and ready to be uploaded.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert variant="destructive">
                        <AlertCircleIcon className="h-4 w-4" />

                        <AlertTitle>Validation Failed</AlertTitle>
                        <AlertDescription>
                          Found {validationResults.invalidRows} issues in your
                          file. Please fix them and try again.
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Validation Summary</h4>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">
                            Total Products
                          </p>
                          <p className="font-medium">
                            {validationResults.totalRows}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Valid Products
                          </p>
                          <p className="font-medium text-green-600 dark:text-green-400">
                            {validationResults.validRows}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">
                            Invalid Products
                          </p>
                          <p className="font-medium text-destructive">
                            {validationResults.invalidRows}
                          </p>
                        </div>
                      </div>
                    </div>

                    {validationResults.errors.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Errors</h4>
                        <div className="border rounded-md overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">Row</TableHead>
                                <TableHead className="w-32">Column</TableHead>
                                <TableHead>Error</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {validationResults.errors.map((error, index) => (
                                <TableRow key={index}>
                                  <TableCell>{error.row}</TableCell>
                                  <TableCell>{error.column}</TableCell>
                                  <TableCell>{error.message}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {uploadState === "success" && (
                  <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400" />

                    <AlertTitle>Upload Successful</AlertTitle>
                    <AlertDescription>
                      {validationResults?.validRows} products have been
                      successfully uploaded.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          {/* Tips Section */}
          <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900">
            <InfoIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />

            <AlertTitle>Tips for Successful Upload</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5 space-y-1 mt-2 text-sm">
                <li>Make sure all required fields are filled correctly</li>
                <li>
                  Use the exact category, subcategory, and product category
                  names as they appear in the system
                </li>
                <li>For condition, use only: New, Used, or Refurbished</li>
                <li>
                  For Yes/No fields, use only "Yes" or "No" (case-sensitive)
                </li>
                <li>For image URLs, separate multiple URLs with commas</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter className="flex justify-between border-t pt-6">
          <Button variant="outline" onClick={resetForm}>
            Cancel
          </Button>
          <Button
            onClick={uploadFile}
            disabled={
              !validationResults?.valid ||
              uploadState === "uploading" ||
              uploadState === "success"
            }
          >
            {uploadState === "uploading" ? "Uploading..." : "Upload Products"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});
